/**
 * 그래프 렌더 준비 유틸 — 노드 중요도(PageRank + degree) 계산 + 색상/크기 부여.
 *
 * 원본: `src/dashboard/src/components/graph/graph-utils.ts` 에서 shared 로 이전.
 * pion-knowledge 버전은 내부 GraphNode/GraphEdge 타입(types.ts)을 사용한다.
 */

import { CATEGORY_COLORS, DOMAIN_COLORS, KIND_COLORS, RELATION_COLORS } from "./graph-colors.ts"
import type { GraphEdge, GraphNode } from "./types.ts"

export { CATEGORY_COLORS, DOMAIN_COLORS, KIND_COLORS, RELATION_COLORS } from "./graph-colors.ts"
export { KIND_TO_LAYER, LAYER_COLORS, LAYER_LABELS, LAYERS, type Layer } from "./graph-layers.ts"

/** 노드 색상 — kind → category → domain → default 순서로 결정 */
export function getNodeColor(node: GraphNode): string {
	return (
		KIND_COLORS[node.kind] ??
		(node.category ? CATEGORY_COLORS[node.category] : undefined) ??
		DOMAIN_COLORS[node.domain] ??
		/* v8 ignore next */
		KIND_COLORS.default ??
		"#6e7681"
	)
}

/** 엣지 색상 — relation 기반 */
function getEdgeColor(edge: GraphEdge): string {
	return RELATION_COLORS[edge.relation] ?? RELATION_COLORS.default ?? "#4b5563"
}

/** 노드별 degree 계산 */
export function computeNodeDegrees(links: ReadonlyArray<GraphEdge>): Map<string, number> {
	const degrees = new Map<string, number>()
	for (const link of links) {
		degrees.set(link.source, (degrees.get(link.source) ?? 0) + 1)
		degrees.set(link.target, (degrees.get(link.target) ?? 0) + 1)
	}
	return degrees
}

/** PageRank — 반복 20회 (대규모는 3회), 감쇠 0.85, 최종 max 기준 정규화 */
export function computePageRank(
	links: ReadonlyArray<GraphEdge>,
	degrees: Map<string, number>,
	iterations = 20,
	damping = 0.85
): Map<string, number> {
	const scores = new Map<string, number>()
	const neighbors = new Map<string, string[]>()

	for (const link of links) {
		if (!neighbors.has(link.source)) neighbors.set(link.source, [])
		if (!neighbors.has(link.target)) neighbors.set(link.target, [])
		neighbors.get(link.source)?.push(link.target)
		neighbors.get(link.target)?.push(link.source)
	}

	const nodeIds = [...degrees.keys()]
	const n = nodeIds.length
	if (n === 0) return scores

	for (const id of nodeIds) scores.set(id, 1 / n)

	for (let iter = 0; iter < iterations; iter++) {
		const newScores = new Map<string, number>()
		for (const id of nodeIds) {
			let sum = 0
			for (const nbr of neighbors.get(id) ?? []) {
				sum += (scores.get(nbr) ?? 0) / (degrees.get(nbr) ?? 1)
			}
			newScores.set(id, (1 - damping) / n + damping * sum)
		}
		for (const [id, val] of newScores) scores.set(id, val)
	}

	let maxScore = 0
	for (const val of scores.values()) {
		if (val > maxScore) maxScore = val
	}
	if (maxScore > 0) {
		for (const [id, val] of scores) scores.set(id, val / maxScore)
	}

	return scores
}

export interface PreparedGraphData {
	nodes: GraphNode[]
	links: GraphEdge[]
}

/**
 * 그래프 데이터를 force-graph 입력 형태로 가공 — 색상/크기/인덱스 부여.
 *
 * 중요: null 프로퍼티에 Object.keys() 를 시도하는 라이브러리와의 호환을 위해
 * 시각화에 필요한 필드만 명시적으로 복사하여 undefined/null 을 빈 문자열로 치환한다.
 */
export function prepareGraphData(
	nodes: ReadonlyArray<GraphNode>,
	edges: ReadonlyArray<GraphEdge>
): PreparedGraphData {
	const nodeIdSet = new Set(nodes.map((n) => n.id))
	const validLinks = edges.filter((e) => nodeIdSet.has(e.source) && nodeIdSet.has(e.target))

	const degrees = computeNodeDegrees(validLinks)
	const pageRanks = computePageRank(validLinks, degrees, nodes.length >= 2000 ? 3 : 20)

	const idxMap = new Map<string, number>()
	const preparedNodes: GraphNode[] = nodes.map((node, i) => {
		idxMap.set(node.id, i)
		const degree = degrees.get(node.id) ?? 0
		const pr = pageRanks.get(node.id) ?? 0
		return {
			id: node.id,
			name: node.name ?? "",
			domain: node.domain ?? "",
			kind: node.kind ?? "",
			category: node.category ?? "",
			content: node.content ?? "",
			metadata: node.metadata ?? "",
			_idx: i,
			_color: getNodeColor(node),
			_size: Math.min(14, 1.0 + Math.sqrt(degree) * 1.2 + pr * 4),
			_labelWeight: pr
		}
	})

	const preparedLinks: GraphEdge[] = validLinks.map((link) => ({
		id: link.id ?? `${link.source}|${link.target}|${link.relation}`,
		source: link.source,
		target: link.target,
		relation: link.relation ?? "",
		_color: getEdgeColor(link),
		/* v8 ignore next 2 — validLinks 필터 후 source/target 은 idxMap 에 항상 존재하는 dead code */
		_srcIdx: idxMap.get(link.source) ?? -1,
		_tgtIdx: idxMap.get(link.target) ?? -1
	}))

	return { nodes: preparedNodes, links: preparedLinks }
}
